import { GRID, LOCAL, orderKey, snap } from "@mn/shared";
import { type Camera, type Rect, screenToPage, zoomAt } from "./camera";
import type { BoardHandle } from "./collab";
import type { SpatialIndex } from "./spatial";
import { useBoardUI } from "./store";

type Mode = "idle" | "pan" | "drag" | "marquee" | "resize";

interface DragState {
  mode: Mode;
  pointerId: number;
  startSX: number;
  startSY: number;
  lastSX: number;
  lastSY: number;
  moved: boolean;
  /** Page-space origins for the cards being dragged. */
  origins: Map<string, { x: number; y: number }>;
  resize?: { id: string; w0: number; h0: number | null; contentHeight: boolean };
  marqueeStart?: { x: number; y: number };
  additive?: boolean;
  /** Touch: card under the finger, pending a tap (select) or a hold (drag). */
  touchCard?: { id: string; locked: boolean };
  hold?: ReturnType<typeof setTimeout>;
}

interface Pinch {
  ids: [number, number];
  dist0: number;
  mid0: { sx: number; sy: number };
  cam0: Camera;
}

const HOLD_MS = 280;
const LONG_PRESS_MS = 500;
const TOUCH_SLOP = 8;

/**
 * All pointer handling for the viewport: pan (space/middle/hand tool or empty-canvas drag with hand),
 * marquee on empty canvas, drag of selected cards, resize via handle. Positions update live in the UI store
 * and are committed to the doc once on release (one undo step, no per-frame doc writes).
 */
export class Interactions {
  private st: DragState | null = null;
  private touches = new Map<number, { sx: number; sy: number }>();
  private pinch: Pinch | null = null;
  private raf = 0;
  private pending: { sx: number; sy: number } | null = null;
  spaceDown = false;

  private viewport: HTMLElement;

  constructor(
    initialViewport: HTMLElement,
    private handle: BoardHandle,
    private index: SpatialIndex,
    private userId: string,
    private canEdit: boolean,
  ) {
    this.viewport = initialViewport;
  }

  /** Capture is best-effort: a pointer that is already gone (or synthetic) must not abort the gesture. */
  private capture(pointerId: number) {
    try {
      this.viewport.setPointerCapture(pointerId);
    } catch {}
  }

  /** The Canvas calls this with its own element so pointer capture lands where the handlers are. */
  attach(el: HTMLElement) {
    this.viewport = el;
  }

  private rel(e: PointerEvent) {
    const r = this.viewport.getBoundingClientRect();
    return { sx: e.clientX - r.left, sy: e.clientY - r.top };
  }
  private cam(): Camera {
    return useBoardUI.getState().camera;
  }

  /** Called by the viewport on pointerdown when the event did not start on a card. */
  onCanvasPointerDown(e: PointerEvent) {
    if (e.pointerType === "touch") {
      if (this.trackTouch(e)) return;
      // One finger on empty canvas pans; holding still opens the context menu.
      useBoardUI.getState().clearSelection();
      this.begin("pan", e);
      const { sx, sy } = this.rel(e);
      this.st!.hold = setTimeout(() => {
        const st = this.st;
        if (!st || st.moved || st.pointerId !== e.pointerId) return;
        const p = screenToPage(this.cam(), sx, sy);
        useBoardUI.getState().setContextMenu({ sx, sy, px: p.x, py: p.y, cardId: null });
        this.st = null;
      }, LONG_PRESS_MS);
      return;
    }
    if (e.button === 1 || this.spaceDown || useBoardUI.getState().tool === "hand" || e.button === 2) {
      if (e.button === 2) return;
      this.begin("pan", e);
      return;
    }
    if (e.button !== 0) return;
    const ui = useBoardUI.getState();
    if (!e.shiftKey && !e.metaKey && !e.ctrlKey) ui.clearSelection();
    const { sx, sy } = this.rel(e);
    this.begin("marquee", e);
    this.st!.marqueeStart = screenToPage(this.cam(), sx, sy);
    this.st!.additive = e.shiftKey || e.metaKey || e.ctrlKey;
  }

  /** Called by a card on pointerdown. */
  onCardPointerDown(e: PointerEvent, id: string, locked: boolean) {
    if (e.pointerType === "touch") {
      if (this.trackTouch(e)) return;
      const ui = useBoardUI.getState();
      if (ui.editingId === id) return;
      // Finger on a card: a tap selects, a short hold starts a drag, moving right away pans.
      this.begin("idle", e);
      this.st!.touchCard = { id, locked };
      if (this.canEdit && !locked)
        this.st!.hold = setTimeout(() => {
          const st = this.st;
          if (!st || st.moved || st.pointerId !== e.pointerId) return;
          if (!useBoardUI.getState().selection.has(id)) useBoardUI.getState().select([id]);
          st.mode = "drag";
          st.origins = this.dragOrigins();
        }, HOLD_MS);
      return;
    }
    if (e.button === 1 || this.spaceDown || useBoardUI.getState().tool === "hand") {
      this.begin("pan", e);
      return;
    }
    if (e.button !== 0) return;
    const ui = useBoardUI.getState();
    if (ui.editingId === id) return; // editing text: let the editor have the pointer
    if (e.shiftKey || e.metaKey || e.ctrlKey) ui.select([id], { toggle: true });
    else if (!ui.selection.has(id)) ui.select([id]);
    if (!this.canEdit || locked) {
      this.begin("idle", e);
      return;
    }
    this.begin("drag", e);
    this.st!.origins = this.dragOrigins();
  }

  /** Page-space origins of the selected, unlocked cards (column children use their on-screen position). */
  private dragOrigins() {
    const origins = new Map<string, { x: number; y: number }>();
    const cam = this.cam();
    const vpRect = this.viewport.getBoundingClientRect();
    for (const sid of useBoardUI.getState().selection) {
      const c = this.handle.types.cards.get(sid);
      if (!c || c.get("locked")) continue;
      if (c.get("parent")) {
        // Child of a column: use its on-screen position as the drag origin (page coords).
        const el = this.viewport.querySelector(`[data-card="${sid}"]`) as HTMLElement | null;
        if (!el) continue;
        const r = el.getBoundingClientRect();
        origins.set(sid, {
          x: (r.left - vpRect.left) / cam.z - cam.x,
          y: (r.top - vpRect.top) / cam.z - cam.y,
        });
      } else origins.set(sid, { x: c.get("x") as number, y: c.get("y") as number });
    }
    return origins;
  }

  /** Remember a touch point; with two fingers down, switch to pinch-zoom. Returns true when a pinch took over. */
  private trackTouch(e: PointerEvent): boolean {
    const { sx, sy } = this.rel(e);
    this.touches.set(e.pointerId, { sx, sy });
    this.capture(e.pointerId);
    if (this.touches.size < 2) return false;
    if (this.pinch) return true;
    const [a, b] = [...this.touches.entries()].slice(0, 2) as [
      [number, { sx: number; sy: number }],
      [number, { sx: number; sy: number }],
    ];
    this.cancelState();
    this.pinch = {
      ids: [a[0], b[0]],
      dist0: Math.max(1, Math.hypot(a[1].sx - b[1].sx, a[1].sy - b[1].sy)),
      mid0: { sx: (a[1].sx + b[1].sx) / 2, sy: (a[1].sy + b[1].sy) / 2 },
      cam0: this.cam(),
    };
    return true;
  }

  private pinchMove() {
    const pz = this.pinch;
    if (!pz) return;
    const a = this.touches.get(pz.ids[0]);
    const b = this.touches.get(pz.ids[1]);
    if (!a || !b) return;
    const dist = Math.max(1, Math.hypot(a.sx - b.sx, a.sy - b.sy));
    const mid = { sx: (a.sx + b.sx) / 2, sy: (a.sy + b.sy) / 2 };
    const zoomed = zoomAt(pz.cam0, pz.mid0.sx, pz.mid0.sy, pz.cam0.z * (dist / pz.dist0));
    useBoardUI.getState().setCamera(() => ({
      ...zoomed,
      x: zoomed.x + (mid.sx - pz.mid0.sx) / zoomed.z,
      y: zoomed.y + (mid.sy - pz.mid0.sy) / zoomed.z,
    }));
  }

  /** Abort whatever single-pointer gesture is in progress without committing anything. */
  private cancelState() {
    if (this.st?.hold) clearTimeout(this.st.hold);
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.pending = null;
    const ui = useBoardUI.getState();
    ui.setDragging(null);
    ui.setResizing(null);
    ui.setMarquee(null);
    ui.setGuides(null);
    ui.setColumnDrop(null);
    ui.setTileDrop(null);
    this.handle.provider.setAwarenessField("dragging", null);
    this.viewport.style.cursor = "";
    this.st = null;
  }

  onResizePointerDown(e: PointerEvent, id: string) {
    if (e.button !== 0 || !this.canEdit) return;
    const c = this.handle.types.cards.get(id);
    if (!c) return;
    this.begin("resize", e);
    this.st!.resize = {
      id,
      w0: c.get("w") as number,
      h0: (c.get("h") as number | null) ?? null,
      contentHeight: c.get("h") == null,
    };
    e.stopPropagation();
  }

  private begin(mode: Mode, e: PointerEvent) {
    const { sx, sy } = this.rel(e);
    this.capture(e.pointerId);
    this.st = {
      mode,
      pointerId: e.pointerId,
      startSX: sx,
      startSY: sy,
      lastSX: sx,
      lastSY: sy,
      moved: false,
      origins: new Map(),
    };
    if (mode === "pan") this.viewport.style.cursor = "grabbing";
  }

  onPointerMove(e: PointerEvent) {
    if (e.pointerType === "touch" && this.touches.has(e.pointerId)) {
      const { sx, sy } = this.rel(e);
      this.touches.set(e.pointerId, { sx, sy });
      if (this.pinch) {
        this.pinchMove();
        return;
      }
    }
    if (!this.st || e.pointerId !== this.st.pointerId) return;
    const { sx, sy } = this.rel(e);
    if (
      this.st.touchCard &&
      this.st.mode === "idle" &&
      Math.hypot(sx - this.st.startSX, sy - this.st.startSY) > TOUCH_SLOP
    ) {
      // Moved before the hold fired: this finger is panning, not dragging the card.
      if (this.st.hold) clearTimeout(this.st.hold);
      this.st.hold = undefined;
      this.st.mode = "pan";
    }
    this.pending = { sx, sy };
    if (!this.raf) this.raf = requestAnimationFrame(this.flush);
  }

  private flush = () => {
    this.raf = 0;
    const st = this.st;
    const p = this.pending;
    if (!st || !p) return;
    const dx = p.sx - st.startSX;
    const dy = p.sy - st.startSY;
    if (!st.moved && Math.hypot(dx, dy) < 4) return;
    st.moved = true;
    const ui = useBoardUI.getState();
    const z = ui.camera.z;
    switch (st.mode) {
      case "pan": {
        const ddx = p.sx - st.lastSX;
        const ddy = p.sy - st.lastSY;
        ui.setCamera((c) => ({ ...c, x: c.x + ddx / z, y: c.y + ddy / z }));
        break;
      }
      case "drag": {
        const next = new Map<string, { x: number; y: number }>();
        for (const [id, o] of st.origins) next.set(id, { x: snap(o.x + dx / z), y: snap(o.y + dy / z) });
        this.snapToNeighbours(next, st.origins, z);
        ui.setDragging(next);
        this.handle.provider.setAwarenessField(
          "dragging",
          [...next].map(([id, pos]) => ({ id, ...pos })),
        );
        ui.setColumnDrop(this.columnTarget(p.sx, p.sy, st.origins));
        ui.setTileDrop(this.tileTarget(p.sx, p.sy, st.origins));
        break;
      }
      case "marquee": {
        const a = st.marqueeStart!;
        const b = screenToPage(ui.camera, p.sx, p.sy);
        const r: Rect = {
          x: Math.min(a.x, b.x),
          y: Math.min(a.y, b.y),
          w: Math.abs(b.x - a.x),
          h: Math.abs(b.y - a.y),
        };
        ui.setMarquee(r);
        ui.select(this.index.inRect(r), { add: st.additive });
        break;
      }
      case "resize": {
        const r = st.resize!;
        const w = Math.max(60, snap(r.w0 + dx / z));
        const h = r.contentHeight ? null : Math.max(40, snap((r.h0 ?? 0) + dy / z));
        ui.setResizing({ id: r.id, w, h });
        break;
      }
    }
    st.lastSX = p.sx;
    st.lastSY = p.sy;
  };

  /**
   * Alignment snapping: when the dragged group's left/centre/right (or top/middle/bottom) comes within
   * 6 screen px of a nearby card's edge or centre, nudge the group onto it and show a guide line.
   */
  private snapToNeighbours(
    next: Map<string, { x: number; y: number }>,
    origins: Map<string, { x: number; y: number }>,
    z: number,
  ) {
    const ui = useBoardUI.getState();
    if (next.size === 0 || next.size > 12) return ui.setGuides(null);
    // Bounding box of the dragged group at its tentative position.
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;
    for (const [id, pos] of next) {
      const r = this.index.rectOf(id);
      const w = r?.w ?? 200;
      const h = r?.h ?? 80;
      minX = Math.min(minX, pos.x);
      minY = Math.min(minY, pos.y);
      maxX = Math.max(maxX, pos.x + w);
      maxY = Math.max(maxY, pos.y + h);
    }
    const threshold = 6 / z;
    const near = this.index
      .inRect({ x: minX - 600, y: minY - 600, w: maxX - minX + 1200, h: maxY - minY + 1200 })
      .filter((id) => !next.has(id) && !origins.has(id));
    const xs = [minX, (minX + maxX) / 2, maxX];
    const ys = [minY, (minY + maxY) / 2, maxY];
    let bestX: { d: number; delta: number; line: number } | null = null;
    let bestY: { d: number; delta: number; line: number } | null = null;
    for (const id of near) {
      const r = this.index.rectOf(id);
      if (!r) continue;
      for (const cx of [r.x, r.x + r.w / 2, r.x + r.w])
        for (const mine of xs) {
          const d = Math.abs(cx - mine);
          if (d <= threshold && (!bestX || d < bestX.d)) bestX = { d, delta: cx - mine, line: cx };
        }
      for (const cy of [r.y, r.y + r.h / 2, r.y + r.h])
        for (const mine of ys) {
          const d = Math.abs(cy - mine);
          if (d <= threshold && (!bestY || d < bestY.d)) bestY = { d, delta: cy - mine, line: cy };
        }
    }
    if (!bestX && !bestY) return ui.setGuides(null);
    for (const pos of next.values()) {
      if (bestX) pos.x += bestX.delta;
      if (bestY) pos.y += bestY.delta;
    }
    ui.setGuides({ v: bestX ? [bestX.line] : [], h: bestY ? [bestY.line] : [] });
  }

  /** Prevent-overlap: cards the dropped ones now cover are pushed down below them (cascading). */
  private pushDown(moved: Set<string>) {
    const { cards } = this.handle.types;
    const skip = (id: string) => {
      const c = cards.get(id);
      const t = c?.get("type") as string | undefined;
      return (
        !c || c.get("locked") || c.get("parent") || c.get("unsorted") || t === "annotation" || t === "line"
      );
    };
    // Positions changed inside this transaction are not in the index yet: keep them here.
    const newY = new Map<string, number>();
    const rectOf = (id: string): Rect | null => {
      const r = this.index.rectOf(id);
      if (!r) return null;
      const y = newY.get(id);
      return y === undefined ? r : { ...r, y };
    };
    const overlaps = (a: Rect, b: Rect) =>
      a.x < b.x + b.w - 1 && a.x + a.w - 1 > b.x && a.y < b.y + b.h - 1 && a.y + a.h - 1 > b.y;
    const queue = [...moved].filter((id) => !skip(id));
    let ops = 0;
    while (queue.length && ops < 80) {
      const id = queue.shift()!;
      const r = rectOf(id);
      if (!r) continue;
      // Candidates from the index (unmoved cards are exact there) plus everything already moved here.
      const candidates = new Set([
        ...this.index.inRect({ x: r.x, y: r.y, w: r.w, h: r.h + 4000 }),
        ...newY.keys(),
      ]);
      const hits = [...candidates]
        .filter((o) => o !== id && !moved.has(o) && !skip(o))
        .map((o) => ({ o, rect: rectOf(o) }))
        .filter((h): h is { o: string; rect: Rect } => !!h.rect && overlaps(r, h.rect))
        .sort((a, b) => a.rect.y - b.rect.y);
      for (const h of hits) {
        const ny = snap(r.y + r.h + GRID);
        if (ny <= h.rect.y) continue;
        cards.get(h.o)!.set("y", ny);
        newY.set(h.o, ny);
        ops++;
        if (!queue.includes(h.o)) queue.push(h.o);
      }
    }
  }

  /** Column under the pointer (excluding dragged cards) and the insertion index from child midpoints. */
  private columnTarget(
    sx: number,
    sy: number,
    dragged: Map<string, unknown>,
  ): { columnId: string; index: number } | null {
    const vp = this.viewport.getBoundingClientRect();
    const els = document.elementsFromPoint(sx + vp.left, sy + vp.top);
    for (const el of els) {
      const colEl = (el as HTMLElement).closest?.("[data-column]") as HTMLElement | null;
      if (!colEl) continue;
      const columnId = colEl.dataset.column!;
      const card = colEl.closest("[data-card]") as HTMLElement | null;
      if (!card || dragged.has(card.dataset.card!) || dragged.has(columnId)) continue;
      const ids = (colEl.dataset.columnChildren ?? "").split(",").filter((x) => x && !dragged.has(x));
      let index = 0;
      for (const cid of ids) {
        const r = (
          colEl.querySelector(`[data-card="${cid}"]`) as HTMLElement | null
        )?.getBoundingClientRect();
        if (r && sy + vp.top > r.top + r.height / 2) index++;
        else break;
      }
      return { columnId, index };
    }
    return null;
  }

  /** Board tile under the pointer (not one of the dragged cards). */
  private tileTarget(sx: number, sy: number, dragged: Map<string, unknown>): string | null {
    const vp = this.viewport.getBoundingClientRect();
    for (const el of document.elementsFromPoint(sx + vp.left, sy + vp.top)) {
      const tile = (el as HTMLElement).closest?.("[data-tile]") as HTMLElement | null;
      if (!tile) continue;
      const card = tile.closest("[data-card]") as HTMLElement | null;
      if (card && dragged.has(card.dataset.card!)) continue;
      return tile.dataset.tile ?? null;
    }
    return null;
  }

  onPointerUp(e: PointerEvent) {
    if (e.pointerType === "touch") {
      this.touches.delete(e.pointerId);
      if (this.pinch) {
        if (this.touches.size < 2) this.pinch = null;
        return;
      }
    }
    const st = this.st;
    if (!st || e.pointerId !== st.pointerId) return;
    if (st.hold) clearTimeout(st.hold);
    if (st.touchCard && st.mode === "idle" && !st.moved) {
      // A tap on a card: select it (tapping a selected card again keeps it selected).
      const ui = useBoardUI.getState();
      if (!ui.selection.has(st.touchCard.id)) ui.select([st.touchCard.id]);
      this.viewport.releasePointerCapture(e.pointerId);
      this.st = null;
      return;
    }
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (this.pending && st.moved) this.flush();
    this.viewport.releasePointerCapture(e.pointerId);
    this.viewport.style.cursor = "";
    const ui = useBoardUI.getState();
    const { doc, types } = this.handle;
    if (st.mode === "drag" && st.moved && ui.dragging && ui.tileDrop) {
      // Dropped on a board tile: hand the cards to that board (async) and skip the position commit.
      const targetBoard = ui.tileDrop;
      const ids = [...ui.dragging.keys()];
      ui.setDragging(null);
      ui.setColumnDrop(null);
      ui.setTileDrop(null);
      ui.setMarquee(null);
      this.handle.provider.setAwarenessField("dragging", null);
      this.st = null;
      window.dispatchEvent(new CustomEvent("mn:move-to-board", { detail: { boardId: targetBoard, ids } }));
      return;
    }
    if (st.mode === "drag" && st.moved && ui.dragging) {
      const moves = ui.dragging;
      const target = ui.columnDrop;
      doc.transact(() => {
        if (target) {
          // Drop into a column at the computed index: reparent and assign fractional order keys.
          const siblings = [...types.cards.entries()]
            .filter(([sid, c]) => c.get("parent") === target.columnId && !moves.has(sid))
            .sort((a, b) => ((a[1].get("order") as string) < (b[1].get("order") as string) ? -1 : 1));
          const prev = siblings[target.index - 1]?.[1].get("order") as string | undefined;
          const next = siblings[target.index]?.[1].get("order") as string | undefined;
          let last = prev ?? null;
          for (const [id] of moves) {
            const c = types.cards.get(id);
            if (!c || c.get("type") === "column") continue;
            last = orderKey(last, next ?? null, doc.clientID);
            c.set("parent", target.columnId);
            c.set("order", last);
            c.set("updatedBy", this.userId);
            c.set("updatedAt", Date.now());
          }
        } else {
          for (const [id, pos] of moves) {
            const c = types.cards.get(id);
            if (!c) continue;
            c.set("parent", null);
            c.set("order", null);
            c.set("x", pos.x);
            c.set("y", pos.y);
            c.set("updatedBy", this.userId);
            c.set("updatedAt", Date.now());
          }
        }
      }, LOCAL);
      // After the commit the spatial index is fresh, so overlap checks see the new positions.
      if (!target && types.meta.get("preventOverlap") !== false)
        doc.transact(() => this.pushDown(new Set(moves.keys())), LOCAL);
      this.handle.undo.stopCapturing();
    }
    if (st.mode === "resize" && ui.resizing) {
      const r = ui.resizing;
      doc.transact(() => {
        const c = types.cards.get(r.id);
        if (!c) return;
        c.set("w", r.w);
        if (r.h != null) c.set("h", r.h);
      }, LOCAL);
      this.handle.undo.stopCapturing();
    }
    this.handle.provider.setAwarenessField("dragging", null);
    ui.setDragging(null);
    ui.setResizing(null);
    ui.setMarquee(null);
    ui.setGuides(null);
    ui.setColumnDrop(null);
    ui.setTileDrop(null);
    this.st = null;
  }

  onPointerCancel(e: PointerEvent) {
    if (e.pointerType === "touch") {
      this.touches.delete(e.pointerId);
      if (this.pinch && this.touches.size < 2) this.pinch = null;
    }
    if (!this.st || e.pointerId !== this.st.pointerId) return;
    if (this.st.hold) clearTimeout(this.st.hold);
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.viewport.style.cursor = "";
    const ui = useBoardUI.getState();
    ui.setDragging(null);
    ui.setResizing(null);
    ui.setMarquee(null);
    ui.setGuides(null);
    ui.setColumnDrop(null);
    this.handle.provider.setAwarenessField("dragging", null);
    this.st = null;
  }

  get active() {
    return this.st?.mode ?? "idle";
  }
}
